import { Link } from "react-router-dom";
import { Heart, Pencil, Share2, Coins, Crown, Users, Image as ImageIcon } from "lucide-react";
import { imgSrc } from "@/lib/api";
import { levelFor } from "@/lib/levels";
import { ClimberBadges } from "@/components/LevelUp";
import { TreasureBadge } from "@/components/Treasure";
import { DropBadges } from "@/components/drops/Drops";
import { FriendButton } from "@/components/FriendButton";
import { DuelButton, DuelBadges } from "@/components/games/DuelButton";
import { QuestHeroBadge } from "@/components/quest/QuestHeroBadge";
import { AppBadge } from "@/components/AppBadge";
import { HeartBadge } from "@/components/HeartBadge";
import { BlockButton } from "@/components/BlockButton";
import { AllInProfileButton } from "@/components/games/AllInGifts";
import { shareProfile, FollowButton } from "@/components/profile/FbProfile";
import { GoldCheck } from "@/components/GoldCheck";
import { FloatingHearts, HeartFrameAvatar } from "./SupporterHeartArt";
import { DankeBanner } from "./SupporterThanks";
import { SpHeart } from "@/components/SpHeart";
import { Glitter } from "@/components/SupporterGlitter";
import { FounderBadge } from "@/components/FounderBadge";
import { AdminTeamBadge } from "@/components/AdminTeamBadge";

const monthYear = (s) => new Date(s).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
const btn = "h-10 px-4 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-[background-color,transform] active:scale-95";
const soft = `${btn} bg-white text-[#7a2e48] ring-1 ring-pink-200 hover:bg-rose-50`;

function Cover({ p }) {
  return (
    <div className="relative h-44 sm:h-64 sp-cover overflow-hidden" data-testid="profile-cover">
      {p.cover && <img src={imgSrc(p.cover)} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="profile-cover-img" />}
      <div className="absolute inset-0 bg-gradient-to-t from-[#9d1446]/45 via-rose-400/10 to-transparent" aria-hidden />
      <FloatingHearts />
      <span data-testid="profile-vip-ribbon" className="absolute top-3 left-3 sp-ribbon inline-flex items-center gap-1.5 pl-2 pr-3 h-8 rounded-full">
        <Heart className="w-4 h-4 text-rose-500" fill="currentColor" /><span className="text-[11px] font-black tracking-[0.18em] sp-gold-text">PROJEKT UNTERSTÜTZER</span>
      </span>
      {p.is_me && <Link to="/profil" data-testid="profile-cover-edit" className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-white/90 text-xs font-bold text-[#7a2e48] hover:bg-white"><ImageIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Titelbild</span></Link>}
      <svg viewBox="0 0 400 40" preserveAspectRatio="none" className="absolute -bottom-px inset-x-0 w-full h-8 sm:h-10" aria-hidden>
        <defs><linearGradient id="sp-wave" x1="0" x2="1"><stop offset="0" stopColor="#f9a8c9" /><stop offset=".5" stopColor="#e6b85c" /><stop offset="1" stopColor="#f9a8c9" /></linearGradient></defs>
        <path d="M0 14 Q100 40 200 22 T400 14 L400 40 L0 40 Z" fill="#fffaf8" />
        <path d="M0 14 Q100 40 200 22 T400 14" fill="none" stroke="url(#sp-wave)" strokeWidth="2.5" />
      </svg>
    </div>
  );
}

const Stat = ({ icon: I, value, label, testId }) => (
  <div className="rounded-2xl bg-white/80 ring-1 ring-pink-100 px-2 py-2.5 text-center min-w-0" data-testid={testId}>
    <p className="font-display text-lg sm:text-xl font-black text-[#3d1728] flex items-center justify-center gap-1 tabular-nums"><I className="w-4 h-4 text-amber-500 shrink-0" /> <span className="truncate">{value}</span></p>
    <p className="text-[11px] sm:text-xs font-semibold text-[#9a5470] truncate">{label}</p>
  </div>
);

function Stats({ p }) {
  const lvl = levelFor(p.points ?? 0);
  return (
    <div className="grid grid-cols-2 min-[440px]:grid-cols-4 gap-2 max-w-xl" data-testid="public-profile-stats">
      <Stat icon={Coins} value={p.is_creator ? "★" : (p.score ?? 0).toLocaleString("de-DE")} label="Punkte" testId="public-stat-score" />
      <Stat icon={Crown} value={`Lv ${lvl.level}`} label={lvl.name} testId="public-profile-level" />
      <Stat icon={Users} value={p.followers ?? p.friends} label="Follower" testId="public-stat-followers" />
      <Stat icon={Heart} value={p.following ?? p.friends} label="Folgt" testId="public-stat-following" />
    </div>
  );
}

function Actions({ p, onChanged }) {
  if (p.is_me) return (
    <div className="flex gap-2 flex-wrap">
      <Link to="/profil" data-testid="public-profile-edit-btn" className={`${btn} pink-gradient text-white shadow-md shadow-rose-200`}><Pencil className="w-4 h-4" /> Profil bearbeiten</Link>
      <button onClick={() => shareProfile(p)} data-testid="public-profile-share-btn" className={soft}><Share2 className="w-4 h-4" /> Teilen</button>
    </div>
  );
  return (
    <div className="flex gap-2 flex-wrap" data-testid="supporter-actions">
      {!p.blocked_by_me && <FollowButton p={p} onChanged={onChanged} />}
      {!p.blocked_by_me && <Link to={`/nachrichten/${p.user_id}`} data-testid="public-profile-message-btn" className={soft}>Nachricht</Link>}
      {!p.blocked_by_me && <DuelButton key={p.user_id} userId={p.user_id} name={p.name} className={soft} />}
      {!p.blocked_by_me && <AllInProfileButton p={p} className={soft} />}
      <button onClick={() => shareProfile(p)} data-testid="public-profile-share-btn" aria-label="Profil teilen" className={soft}><Share2 className="w-4 h-4" /></button>
      <BlockButton p={p} onChanged={onChanged} className={btn} />
    </div>
  );
}

const Badges = ({ p }) => (
  <div className="flex flex-wrap gap-1.5" data-testid="supporter-achievements-row">
    <ClimberBadges badges={p.climber_badges} testId="public-climber-badges" /><TreasureBadge streak={p.treasure_streak} testId="public-treasure-badge" /><DuelBadges badges={p.duel_badges} testId="public-duel-badges" />
    <QuestHeroBadge items={p.quest_hero} testId="public-quest-hero-badge" /><AppBadge show={p.app_user} testId="public-app-badge" /><HeartBadge count={p.heart_count} testId="public-heart-badge" /><DropBadges badges={p.drop_badges} testId="public-drop-badges" />
  </div>
);

const HeartsCounter = ({ n }) => (
  <span className="relative mt-2 inline-flex items-center gap-1.5 h-9 pl-2 pr-3.5 rounded-full sp-surface sp-notif sp-glow sp-glow-sm text-sm font-extrabold" data-testid="supporter-hearts-counter" title="Rosé-Gold-Herzen von Projekt Unterstützern">
    <Glitter size="sm" /><SpHeart className="w-6 h-6" /><span className="tabular-nums" data-testid="supporter-hearts-count">{n.toLocaleString("de-DE")}</span> {n === 1 ? "Rosé-Gold-Herz" : "Rosé-Gold-Herzen"} erhalten
  </span>
);

export function SupporterProfileHeader({ p, onChanged }) {
  return (
    <div className="relative sp-card rounded-b-3xl sm:rounded-3xl overflow-hidden -mx-4 sm:mx-0" data-testid="public-profile-card" data-vip>
      <Cover p={p} />
      <div className="relative px-4 sm:px-8 pb-6">
        <div className="flex flex-col sm:flex-row items-center sm:items-end gap-2 sm:gap-6 -mt-24 sm:-mt-28">
          <HeartFrameAvatar p={p} />
          <div className="flex-1 min-w-0 text-center sm:text-left sm:pb-6">
            <h1 className="hero-plain font-display text-2xl sm:text-4xl font-black tracking-tight inline-flex items-center gap-2 flex-wrap justify-center sm:justify-start" data-testid="public-profile-name">
              <span className="sp-name min-w-0 break-words" data-testid="supporter-name">{p.name}</span>
              <GoldCheck className="w-8 h-8 sm:w-9 sm:h-9" testId="public-profile-role-supporter" />
              <FounderBadge userId={p.user_id} size="lg" testId="public-profile-founder-badge" />
              <AdminTeamBadge userId={p.user_id} size="lg" testId="public-profile-admin-badge" />
            </h1>
            {p.is_top_supporter && <span data-testid="profile-top-supporter-label" className="mt-1 inline-flex items-center gap-1 text-[10px] leading-5 px-2.5 rounded-full font-extrabold tracking-[0.18em] text-[#7a2e48] bg-gradient-to-r from-rose-100 to-amber-100 ring-1 ring-amber-300">TOP UNTERSTÜTZER <Heart className="w-3 h-3 text-rose-500" fill="currentColor" /></span>}
            <p className="mt-1 text-sm font-semibold text-[#9a5470]" data-testid="vip-member-since">Projekt Unterstützer · Mitglied seit {monthYear(p.created_at)}</p>
            {p.sp_hearts > 0 && <HeartsCounter n={p.sp_hearts} />}
          </div>
        </div>
        <div className="mt-4 space-y-4">
          <DankeBanner p={p} />
          {p.bio && <p className="text-sm text-[#5b1f35] whitespace-pre-wrap max-w-xl" data-testid="public-profile-bio">{p.bio}</p>}
          <Stats p={p} />
          <Badges p={p} />
          <Actions p={p} onChanged={onChanged} />
          {!p.is_me && !p.blocked_by_me && <div data-testid="public-profile-friend-row"><FriendButton userId={p.user_id} onChange={onChanged} noMessage /></div>}
        </div>
      </div>
    </div>
  );
}
